import clsx, { ClassValue } from "clsx";
import { format } from "date-fns";
import { twMerge } from "tailwind-merge";
import { formatInTimeZone as dateFnsFormatInTimeZone } from "date-fns-tz";
import { IPracticeBaseSession } from "../../modules/auth/types/auth.types";
import {
    FormStatus,
    PORTAL_SUPPORTED_FORM_TYPES,
} from "../../modules/client-forms/types/client-forms.types";
import { ROIForm } from "../../modules/upcoming-sessions/types/upcoming-sessions.types";

/** FORMAT MONEY VALUES TO APP CURRENCY DISPLAY  */
export const showMoneyInAppFormat = (amount: string | number) => {
    // Convert amount to valid number
    const convertedAmount = Number(amount);

    // Check if converted value is a valid number
    const isValidNumber =
        !Number.isNaN(convertedAmount) && Number.isFinite(convertedAmount);

    // Format amount to desired style
    if (isValidNumber) {
        return new Intl.NumberFormat("us-EN", {
            style: "currency",
            currency: "USD",
        })
            .format(convertedAmount)
            .replace("US", "");
    }
    return "--";
};

/** Helps with
 * - Avoiding tailwind classes conflict
 * - Add classes based on different conditions
 */
export const cn = (...args: ClassValue[]) => {
    return twMerge(clsx(args));
};

/** FORMAT DATE PICKER VALUE TO REQUIRED FORMAT - returns string `year-month-day` */
export const handleFormatDatePickerValue = (inputValue: string | Date) => {
    return format(new Date(inputValue), "yyyy-MM-dd");
};

// Function to display user initials in the right format
export const userInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0).toUpperCase()}${lastName
        .charAt(0)
        .toUpperCase()}`;
};

export const getClientDisplayName = (
    preferredName?: string | null,
    firstName?: string,
    lastName?: string,
    fallbackType: "first_name" | "full_name" = "first_name"
): string => {
    if (preferredName?.trim()) return preferredName.trim();
    const first = firstName?.trim() || "";
    if (fallbackType === "first_name") return first;
    return [first, lastName?.trim()].filter(Boolean).join(" ");
};

export const getClientInitials = (
    preferredName?: string | null,
    firstName?: string,
    lastName?: string
): string => {
    const preferred = preferredName?.trim();
    if (preferred) {
        const [firstPart, ...rest] = preferred.split(/\s+/);
        return userInitials(
            firstPart,
            rest.length ? rest[rest.length - 1] : ""
        );
    }
    return userInitials(firstName?.trim() || "", lastName?.trim() || "");
};

/** REMOVE EMPTY QUERY PARAMS FROM PARAMS OBJECT */
export const handleRemoveEmptyParamFromQueryParams = <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T extends Record<string, any>,
>(
    paramsObj: T
) => {
    return Object.fromEntries(
        Object.entries(paramsObj).filter(([, v]) => {
            if (typeof v === "string") return v !== "";
            if (Array.isArray(v)) return v.length !== 0;
            return Boolean(v) !== false;
        })
    );
};

/** REMOVES UNDERSCORE FROM ENUM VALUES */
export const removeEnumUnderscore = (value: string) => {
    return value?.replace(/_/g, " ");
};

/** FORMAT DATETIME IN THE RIGHT TIMEZONE */
// Fomat utc time to "America/New_York" timezone
export const formatDate = (
    isoDate: string,
    doNotConvertToTz = false,
    dateFormat = "MM/dd/yyyy",
    timeZone = "America/New_York"
) => {
    if (!Date.parse(isoDate)) return "--";

    if (doNotConvertToTz) {
        if (typeof isoDate === "string" && isoDate?.includes("Z")) {
            return format(
                new Date(isoDate.replace("Z", "")),
                dateFormat || "MM/dd/yyyy"
            );
        }
        return format(new Date(isoDate), dateFormat || "MM/dd/yyyy");
    }

    // Create a new Date object without adjusting the time
    const date = new Date(isoDate);

    return dateFnsFormatInTimeZone(date, timeZone, dateFormat || "MM/dd/yyyy");
};

/** GET AMOUNT OF ROW SELECTED IN A TABLE */
export const getTotalAmountOfTableRowsSelected = (
    selectedState: Record<number, boolean>
) => {
    const totalRowsSelected = Object.keys(selectedState).length;

    return {
        amountString: `${totalRowsSelected} selected`,
        amount: totalRowsSelected,
    };
};

/** RETURNS A DATE FILTER STRING VALUE (YYYY-MM-DD) AS A DATE SO WE CAN USE IT
 * IN THE DATE PICKER COMPONENT AND DISPLAY THE RIGHT DATE
 */
export const convertDateFilterStringToDate = (
    dateFilterValue: string | null
): Date | null => {
    if (!dateFilterValue) {
        return null;
    }

    const date = new Date(dateFilterValue);
    const minDate = new Date("1900-01-01");

    if (date < minDate) {
        return null;
    }

    return new Date(`${dateFilterValue}T00:00:00`);
};

// function to truncate string length
export const truncateString = (str: string, length = 8) => {
    if (str.length === length || str.length < length) return str;

    return `${str.slice(0, length)}...`;
};

export function displayNameInRightFormat(
    user: string | { firstName: string; lastName: string }
) {
    if (typeof user === "string") {
        const splitedNames = user.split(" ");
        if (splitedNames.length > 2) {
            const otherNames = splitedNames.slice(1).join(" ");
            return `${splitedNames[0].toUpperCase()}, ${otherNames}`;
        }
        const [nameOne, nameTwo] = user.split(" ");
        return `${nameOne.toUpperCase()}, ${nameTwo}`;
    }

    return `${user.lastName.toUpperCase()}, ${user.firstName}`;
}

export function formatNoteType(noteType: string) {
    if (!noteType) return "--";

    const withoutNote = noteType.endsWith("_note")
        ? noteType.slice(0, -5)
        : noteType;

    return removeEnumUnderscore(withoutNote);
}

/** CAPITALIZE THE FIRST LETTER OF A STRING */
export const makeStringFirstLetterCapital = (str: string) => {
    if (!str) return "";
    const words = str.split(/(?=[A-Z])/); // Split string at capital letters
    if (words.length > 1) {
        // If there are multiple words, capitalize the first letter of each word
        const capitalizedWords = words.map((word) => {
            const stringFirstLetter = word[0];
            const capitalizedStringFirstLetter =
                stringFirstLetter?.toUpperCase();
            const remainingStringLetters = word?.slice(1);
            return `${capitalizedStringFirstLetter}${remainingStringLetters}`;
        });

        return capitalizedWords.join(" ");
    }
    // If there is only one word, capitalize the first letter
    const stringFirstLetter = str[0];
    const capitalizedStringFirstLetter = stringFirstLetter?.toUpperCase();
    const remainingStringLetters = str?.slice(1);

    return `${capitalizedStringFirstLetter}${remainingStringLetters}`;
};

export const areArraysEqual = <T>(arr1: T[], arr2: T[]): boolean => {
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;
    const set1 = new Set(arr1);
    return arr2.every((value) => set1.has(value));
};

export const getROILink = (practiceName: string): string => {
    const name = practiceName.toLowerCase();
    if (name.includes("carnegie hill") || name.includes("chp"))
        return ROIForm.CHP;
    if (name.includes("park hill")) return ROIForm.PH;
    return ROIForm.HT;
};

export function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
}

const CONSENT_DONE_STATUSES = new Set([
    FormStatus.RECEIVED,
    FormStatus.COMPLETED,
    FormStatus.PAUSED,
    FormStatus.CANCELLED,
]);

export function getSessionsNeedingConsent(
    sessions: IPracticeBaseSession[]
): IPracticeBaseSession[] {
    return sessions.filter((session) => {
        const { consent } = session;
        if (!consent) return false;

        const hasExternalLink = Boolean(consent.consent_form_link?.trim());
        const consentFormType = consent.consent_form_type;
        const hasInternalLink =
            Boolean(session.booking_id) &&
            consentFormType !== undefined &&
            PORTAL_SUPPORTED_FORM_TYPES.has(consentFormType);

        return (
            (hasExternalLink || hasInternalLink) &&
            !CONSENT_DONE_STATUSES.has(consent.status) &&
            !consent.client_submitted
        );
    });
}

export const base64ToBlob = (base64: string, contentType = "") =>
    new Blob([Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))], {
        type: contentType,
    });
